import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useLocale } from "../../i18n/locale-context";
import { menuKeyIndex } from "../../lib/menuKeys";
import { InboxIcon, PaperPlaneIcon, CheckIcon } from "../icons";

type Channel = "email" | "telegram";
const CHANNELS: readonly Channel[] = ["email", "telegram"];

/** Horizontal tablist keys, mapped onto the shared menu-key helper. */
const TAB_KEYS: Record<string, string> = { ArrowRight: "ArrowDown", ArrowLeft: "ArrowUp", Home: "Home", End: "End" };

const prefersReducedMotion = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);

const tabId = (ch: Channel) => `hero-demo-tab-${ch}`;
const panelId = (ch: Channel) => `hero-demo-panel-${ch}`;

/**
 * Email / Telegram example conversation.
 *
 * - The finished conversation is the default: the prerendered HTML and the
 *   page before JavaScript show every message, with no animation.
 * - If the card is already visible when the page loads (above the fold, as on
 *   most desktops), it stays finished: replaying what the visitor is already
 *   reading would look like a flash. If it only comes into view later by
 *   scrolling (typical on phones), it plays once when a third of it is
 *   visible (messages slide in, staggered).
 * - Switching tabs plays the newly shown conversation.
 * - With prefers-reduced-motion it never animates.
 * - Both conversations are stacked in one grid cell (the inactive one is
 *   invisible), so the card is always as tall as the longer one and switching
 *   tabs never moves the page.
 * - English opens on Email; Uzbek and Russian open on Telegram, which is how
 *   customers there write.
 */
export function HeroDemo() {
  const { locale, strings } = useLocale();
  const d = strings.landing.demo;
  const [channel, setChannel] = useState<Channel>(locale === "en" ? "email" : "telegram");
  const [play, setPlay] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (prefersReducedMotion()) return;
    // The first callback reports the state at mount: visible then means the
    // visitor already sees the finished conversation, so leave it alone.
    let first = true;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (first) {
          first = false;
          if (visible) {
            obs.disconnect();
            return;
          }
        }
        if (visible) {
          setPlay(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /** Show a conversation; a tab switch always plays it (unless reduced motion). */
  function select(ch: Channel) {
    if (ch === channel) return;
    setChannel(ch);
    if (!prefersReducedMotion()) setPlay(true);
  }

  function onTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const key = TAB_KEYS[e.key];
    if (!key) return;
    const next = menuKeyIndex(key, index, CHANNELS.length);
    const nextChannel = next === null ? undefined : CHANNELS[next];
    if (next === null || !nextChannel) return;
    e.preventDefault();
    select(nextChannel);
    tabRefs.current[next]?.focus();
  }

  return (
    <div ref={cardRef} className="glass-dark p-4 text-left sm:p-5">
      <div role="tablist" aria-label={d.aria} className="mb-4 inline-flex rounded-full bg-white/5 p-1 ring-1 ring-white/10">
        {CHANNELS.map((ch, i) => {
          const on = ch === channel;
          const Icon = ch === "email" ? InboxIcon : PaperPlaneIcon;
          return (
            <button
              key={ch}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              id={tabId(ch)}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={panelId(ch)}
              tabIndex={on ? 0 : -1}
              onClick={() => select(ch)}
              onKeyDown={(e) => onTabKeyDown(e, i)}
              className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 text-sm font-semibold transition-colors ${
                on ? "bg-white/15 text-white shadow-sm" : "text-slate-300 hover:text-white"
              }`}
            >
              <Icon className="h-4 w-4" />
              {d[ch]}
            </button>
          );
        })}
      </div>

      <div className="grid">
        {CHANNELS.map((ch) => {
          const active = ch === channel;
          const c = ch === "email" ? d.emailConvo : d.telegramConvo;
          // Animation classes go on only while playing, and only on the shown
          // conversation: re-adding them on a tab switch restarts the animation.
          const animate = play && active;
          const a = animate ? "demo-msg " : "";
          const step = (n: number) => (animate ? { animationDelay: `${300 + n * 900}ms` } : undefined);
          return (
            <div
              key={ch}
              id={panelId(ch)}
              role="tabpanel"
              aria-labelledby={tabId(ch)}
              aria-hidden={active ? undefined : true}
              className={`col-start-1 row-start-1 space-y-2.5 ${active ? "" : "invisible"}`}
            >
              <div className={`${a}flex items-center gap-2 text-xs font-semibold text-slate-300`} style={step(0)}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-400/15 text-[11px] font-bold text-sky-200">
                  {c.name.slice(0, 1)}
                </span>
                {c.name}
              </div>
              <p className={`${a}max-w-[85%] rounded-2xl rounded-bl-md bg-white/10 px-3.5 py-2.5 text-sm leading-relaxed text-slate-100`} style={step(0)}>
                {c.customer1}
              </p>
              <p className={`${a}text-center text-[11px] font-medium text-slate-400`} style={step(1)}>
                {d.replied}
              </p>
              <p className={`${a}ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-brand-600 px-3.5 py-2.5 text-sm leading-relaxed text-white`} style={step(1)}>
                {c.reply}
              </p>
              <p className={`${a}max-w-[70%] rounded-2xl rounded-bl-md bg-white/10 px-3.5 py-2.5 text-sm leading-relaxed text-slate-100`} style={step(2)}>
                {c.customer2}
              </p>
              <div className={`${a}pt-1`} style={step(3)}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-400/10 px-3 py-1 text-xs font-semibold text-sky-200 ring-1 ring-sky-300/30">
                  <CheckIcon className="h-3.5 w-3.5" />
                  {d.qualified} · {c.tag}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
